// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — how precise a value read from a release really is, and where it
 * came from (value error audit, 2026-10-19, docs/AYNI_XR_VALUE_ERROR_AUDIT.md;
 * plan docs/AYNI_XR_VALUE_FIDELITY_PLAN.md).
 *
 * - **The step at a value.** One code is a fixed width on a linear release,
 *   but a fixed *ratio* on a log one: 0.1–100 mm/h over 203 codes is 3.5 %
 *   per code, 0.005 mm/h at 0.15 and 3.4 mm/h at 100. Rounding a log reading
 *   to the linear width (0.49 mm/h) showed 0.15 as "0.1" or "0.2" and every
 *   value under 0.5 µg/m³ of PM2.5 as "0" (`codeStepAt`, `scaleCodeStep`).
 * - **The ±.** Half a step of 8-bit rounding, always known from the
 *   descriptor, plus the codec's 99th percentile when the release states it:
 *   measured (error budget v2, in codes), lossless (0), or the builder's
 *   bound (at most one code when under 1 % of pixels are off by more than
 *   one). When the codec error is not published the ± is the rounding part
 *   only and says so (owner, 2026-10-19: "known part + label").
 * - **Custody.** The host's `diagnostics.json` (codec setting) is read once
 *   per release after it resolves; each frame's manifest entry (time, run,
 *   source files, worst code error) is read on demand and kept.
 * - **Exact frames.** When a release publishes its pre-codec codes per frame
 *   (`exactFrames`), a single texel can be read exactly: no codec error.
 */

import { sourceEncoding, sourceCodeStep } from './decoderRange'
import type { ColorScale } from '../types/color-scale'
import { releaseOf } from '../types/color-scale'
import type { ReleaseEncoding } from '../types/release-encoding'
import {
  codecFromDiagnostics,
  parseFrameCustody,
  parseReleaseFidelity,
  type CodecFidelity,
  type FrameCustody,
  type ReleaseFidelity,
} from '../types/release-fidelity'
import { logger } from '../utils/logger'

// ------------------------------------------------------------------ steps

/** The ratio between neighbouring codes of a log release (1.035 for 0.1–100 over 203 codes); null otherwise. */
export function logCodeRatio(enc: ReleaseEncoding): number | null {
  const src = sourceEncoding(enc)
  if (src.kind !== 'luma8-log' || !(src.vmin > 0) || !(src.vmax > src.vmin)) return null
  return Math.pow(src.vmax / src.vmin, 1 / (src.dataMaxCode - src.dataMinCode))
}

/**
 * One code's width at `value`, in the release's units: the fixed source step
 * of a linear release, `value × (ratio − 1)` on a log one, null for classes
 * (a class has no width).
 */
export function codeStepAt(enc: ReleaseEncoding, value: number): number | null {
  const src = sourceEncoding(enc)
  if (src.kind === 'luma8-classified') return null
  if (src.kind === 'luma8-log') {
    const ratio = logCodeRatio(src)
    if (ratio === null) return null
    const v = Math.max(Math.abs(value), src.vmin)
    return v * (ratio - 1)
  }
  const step = Math.abs(sourceCodeStep(src))
  return Number.isFinite(step) && step > 0 ? step : null
}

/**
 * One code's width in a scale's own units (which may restate the release's,
 * °C as °F): the source step of a linear release, null for a log or
 * classified one (no single width), and `(vmax − vmin)/255` for a scale with
 * no release behind it. Replaces `(scale.vmax − scale.vmin)/255`, which for
 * a release is the width of a *decoded* code: 14 % too small under Chrome's
 * limited-range expansion.
 */
export function scaleCodeStep(scale: ColorScale): number | null {
  const range = Math.abs(scale.vmax - scale.vmin)
  if (!(range > 0) || !Number.isFinite(range)) return null
  const release = releaseOf(scale)
  if (!release) return range / 255
  if (release.kind !== 'luma8-linear') return null
  const src = sourceEncoding(release)
  const decodedSpan = release.dataMaxCode - release.dataMinCode
  const sourceSpan = src.dataMaxCode - src.dataMinCode
  if (!(decodedSpan > 0) || !(sourceSpan > 0)) return null
  // The scale spans 255 decoded codes; `decodedSpan` of them hold `sourceSpan` source codes.
  return (range / 255) * (decodedSpan / sourceSpan)
}

// -------------------------------------------------------------- codec, ±

const diagnostics = new Map<string, DiagnosticsFidelity | null>()
const frameManifests = new Map<string, Promise<readonly FrameCustody[]>>()

/** For tests. */
export function resetFidelityCaches(): void {
  diagnostics.clear()
  frameManifests.clear()
  exactFrameCache.clear()
}

/**
 * What the host's `diagnostics.json` adds to a release. It states the codec
 * setting (`crf`, `lossless`) today; until the release schema allows the new
 * keys (the host's schemas are closed), it also carries the proposed error
 * budget v2, `source` and per-frame custody (`frameCustody`), at its top
 * level or under `valueFidelity` (stream host, 2026-10-19: "interim, now").
 */
export interface DiagnosticsFidelity {
  readonly codec: CodecFidelity | null
  readonly source: ReleaseFidelity['source']
  readonly clipping: ReleaseFidelity['clipping']
  readonly regrid: ReleaseFidelity['regrid']
  readonly frames: ReadonlyMap<number, FrameCustody>
}

/** A `diagnostics.json` body as fidelity, or null when it is not an object. */
export function parseDiagnosticsFidelity(body: unknown, url: string | null = null): DiagnosticsFidelity | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null
  const top = body as Record<string, unknown>
  const nested = top.valueFidelity
  const vf = nested && typeof nested === 'object' && !Array.isArray(nested) ? (nested as Record<string, unknown>) : top
  const parsed = parseReleaseFidelity(vf, url)
  const list = Array.isArray(vf.frameCustody) ? vf.frameCustody : Array.isArray(top.frameCustody) ? top.frameCustody : []
  const frames = new Map<number, FrameCustody>()
  for (const raw of list.slice(0, 20000)) {
    const entry = parseFrameCustody(raw, url)
    if (entry) frames.set(entry.index, entry)
  }
  return {
    codec: parsed?.codec ?? codecFromDiagnostics(top),
    source: parsed?.source ?? null,
    clipping: parsed?.clipping ?? null,
    regrid: parsed?.regrid ?? null,
    frames,
  }
}

/**
 * Start what a resolved release needs read beside it: the host's
 * `diagnostics.json` (codec setting, and in the interim the error budget,
 * source and per-frame custody). Never blocks playback; a failure leaves
 * them unknown.
 */
export function startFidelityLoads(fidelity: ReleaseFidelity, fetchImpl: typeof fetch = fetch): void {
  const url = fidelity.diagnosticsUrl
  if (!url || diagnostics.has(url)) return
  diagnostics.set(url, null)
  void fetchImpl(url, { headers: { Accept: 'application/json' } })
    .then((res) => (res.ok ? res.json() : null))
    .then((body) => {
      diagnostics.set(url, parseDiagnosticsFidelity(body, url))
      for (const fn of [...fidelityListeners]) fn(url)
    })
    .catch((err) => logger.debug('[valueFidelity] diagnostics not read', url, err))
}

const fidelityListeners = new Set<(diagnosticsUrl: string) => void>()

/** Be told when a release's diagnostics arrive (the Accuracy section redraws with the codec setting). Returns the unsubscribe. */
export function onFidelityLoaded(fn: (diagnosticsUrl: string) => void): () => void {
  fidelityListeners.add(fn)
  return () => fidelityListeners.delete(fn)
}

/** The release's diagnostics, once read (null before, or when it has none). */
function diagnosticsOf(enc: ReleaseEncoding): DiagnosticsFidelity | null {
  const f = sourceEncoding(enc).fidelity ?? enc.fidelity
  return f?.diagnosticsUrl ? (diagnostics.get(f.diagnosticsUrl) ?? null) : null
}

/**
 * What is known of a release's fidelity: the release's own statement first,
 * else its diagnostics (the host's interim place for them).
 */
export function fidelityOf(enc: ReleaseEncoding): {
  readonly release: ReleaseFidelity | null
  readonly codec: CodecFidelity | null
  readonly source: ReleaseFidelity['source']
  readonly clipping: ReleaseFidelity['clipping']
  readonly regrid: ReleaseFidelity['regrid']
} {
  const f = sourceEncoding(enc).fidelity ?? enc.fidelity ?? null
  const d = diagnosticsOf(enc)
  return {
    release: f,
    codec: f?.codec ?? d?.codec ?? null,
    source: f?.source ?? d?.source ?? null,
    clipping: f?.clipping ?? d?.clipping ?? null,
    regrid: f?.regrid ?? d?.regrid ?? null,
  }
}

/** The release's codec as far as known: its own statement, else the host's diagnostics (once read). */
export function codecOfRelease(enc: ReleaseEncoding): CodecFidelity | null {
  return fidelityOf(enc).codec
}

/** Whether a frame's custody can be looked up: a frame manifest, or per-frame entries in the diagnostics. */
export function hasFrameCustody(enc: ReleaseEncoding): boolean {
  const f = sourceEncoding(enc).fidelity ?? enc.fidelity
  return !!f?.frames || (diagnosticsOf(enc)?.frames.size ?? 0) > 0
}

/** How the codec part of a ± is known. */
export type UncertaintyBasis = 'measured' | 'lossless' | 'bound' | 'roundingOnly'

export interface ReadingUncertainty {
  /** The ±, in the release's units. */
  readonly u: number
  /** True when the codec's part is included (measured, lossless or a stated bound). */
  readonly complete: boolean
  readonly basis: UncertaintyBasis
}

/** The codec's 99th percentile in codes, and how it is known; null when not published. */
export function codecP99Codes(codec: CodecFidelity | null): { codes: number; basis: Exclude<UncertaintyBasis, 'roundingOnly'> } | null {
  if (!codec) return null
  if (codec.lossless === true) return { codes: 0, basis: 'lossless' }
  const c = codec.codes
  if (!c) return null
  if (c.p99 !== null && c.p99 >= 0) return { codes: c.p99, basis: c.max === 0 ? 'lossless' : 'measured' }
  if (c.max === 0) return { codes: 0, basis: 'lossless' }
  // The builder states the share off by more than one code: under 1 % means 99 % are within one.
  if (c.fractionOverOne !== null && c.fractionOverOne < 0.01) return { codes: 1, basis: 'bound' }
  if (c.max !== null && c.max >= 0) return { codes: c.max, basis: 'bound' }
  return null
}

/**
 * The ± of a value read from the video at `value`: half a code plus the
 * codec's 99th percentile in codes, both as widths at that value. Uses the
 * release's error budget v1 (codec p99 in units) when that is all it has.
 * Null for classes and where there is no width.
 */
export function readingUncertainty(enc: ReleaseEncoding, value: number): ReadingUncertainty | null {
  const step = codeStepAt(enc, value)
  if (step === null || !(step > 0)) return null
  const half = step / 2
  const codec = codecP99Codes(codecOfRelease(enc))
  if (codec) return { u: half + codec.codes * step, complete: true, basis: codec.basis }
  const v1 = sourceEncoding(enc).accuracy?.budget.codec?.p99 ?? enc.accuracy?.budget.codec?.p99
  if (sourceEncoding(enc).kind === 'luma8-linear' && v1 !== null && v1 !== undefined && v1 >= 0) {
    return { u: half + v1, complete: true, basis: 'measured' }
  }
  return { u: half, complete: false, basis: 'roundingOnly' }
}

// ---------------------------------------------------------------- frames

async function readManifest(url: string, fetchImpl: typeof fetch): Promise<readonly FrameCustody[]> {
  const res = await fetchImpl(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`frame manifest ${url} answered ${res.status}`)
  const body: unknown = await res.json()
  const list = Array.isArray(body) ? body : (body as { frames?: unknown })?.frames
  return Array.isArray(list) ? list.flatMap((f) => parseFrameCustody(f, url) ?? []) : []
}

function manifest(url: string, fetchImpl: typeof fetch): Promise<readonly FrameCustody[]> {
  let p = frameManifests.get(url)
  if (!p) {
    p = readManifest(url, fetchImpl).catch((err) => {
      frameManifests.delete(url)
      throw err
    })
    frameManifests.set(url, p)
  }
  return p
}

/**
 * A frame's custody entry (time, run, source files, worst code error), or
 * null when the release has no manifest for it. `frameIndex` is the
 * release's global frame index (the host's `globalFrameIndex`, the
 * builder's `frameIndex`).
 */
export async function frameCustody(enc: ReleaseEncoding, frameIndex: number, fetchImpl: typeof fetch = fetch): Promise<FrameCustody | null> {
  const f = sourceEncoding(enc).fidelity ?? enc.fidelity
  const fromDiagnostics = diagnosticsOf(enc)?.frames.get(frameIndex)
  if (fromDiagnostics) return fromDiagnostics
  const frames = f?.frames
  if (!frames || !Number.isFinite(frameIndex)) return null
  try {
    if ('segments' in frames) {
      const seg = frames.segments.find((s) => frameIndex >= s.startFrame && frameIndex < s.startFrame + s.frameCount)
      if (!seg) return null
      const list = seg.inline ?? (seg.manifestUrl ? await manifest(seg.manifestUrl, fetchImpl) : [])
      return list.find((x) => x.index === frameIndex) ?? null
    }
    const list = await manifest(frames.manifestUrl, fetchImpl)
    return list.find((x) => x.index === frameIndex) ?? null
  } catch (err) {
    logger.debug('[valueFidelity] frame custody not read', err)
    return null
  }
}

/**
 * The custody entry of the frame shown at `timeMs` (the time the date track
 * gives): on the host's releases the index follows from the window's start
 * and cadence, then the entry's own time confirms it (a neighbour is tried
 * when it disagrees); the builder's one manifest is searched by time.
 */
export async function frameCustodyAt(enc: ReleaseEncoding, timeMs: number, fetchImpl: typeof fetch = fetch): Promise<FrameCustody | null> {
  const f = sourceEncoding(enc).fidelity ?? enc.fidelity
  if (!f || !hasFrameCustody(enc) || !Number.isFinite(timeMs)) return null
  const near = (x: FrameCustody | null, tolMs: number): boolean => {
    const t = x?.time ? Date.parse(x.time) : NaN
    return Number.isFinite(t) && Math.abs(t - timeMs) <= tolMs
  }
  if (!f.frames || 'segments' in f.frames) {
    const timing = f.timing
    if (!timing) return null
    const cadenceMs = timing.cadenceSeconds * 1000
    const guess = timing.windowStartFrame + Math.round((timeMs - timing.startMs) / cadenceMs)
    for (const idx of [guess, guess - 1, guess + 1]) {
      const entry = await frameCustody(enc, idx, fetchImpl)
      if (near(entry, cadenceMs / 2)) return entry
    }
    return null
  }
  try {
    const list = await manifest(f.frames.manifestUrl, fetchImpl)
    let best: FrameCustody | null = null
    let bestGap = Infinity
    for (const x of list) {
      const gap = x.time ? Math.abs(Date.parse(x.time) - timeMs) : Infinity
      if (gap < bestGap) { best = x; bestGap = gap }
    }
    // A monthly or yearly frame's stamp is its first instant: anything within half a cadence (or 16 days) is that frame.
    const tol = f.timing ? (f.timing.cadenceSeconds * 1000) / 2 : 16 * 86400000
    return bestGap <= tol ? best : null
  } catch (err) {
    logger.debug('[valueFidelity] frame custody not read', err)
    return null
  }
}

// ----------------------------------------------------------- exact frames

const exactFrameCache = new Map<string, Promise<{ width: number; height: number; codes: Uint8Array } | null>>()
const EXACT_CACHE_MAX = 8

/** The URL of a frame's exact codes, or null when the release publishes none. */
export function exactFrameUrl(enc: ReleaseEncoding, frameIndex: number): string | null {
  const f = sourceEncoding(enc).fidelity ?? enc.fidelity
  const exact = f?.exactFrames
  if (!exact || exact.format !== 'png-gray8-codes' || !Number.isInteger(frameIndex) || frameIndex < 0) return null
  try {
    return new URL(exact.template.replace('{frame}', String(frameIndex)), f!.releaseUrl ?? undefined).toString()
  } catch {
    return null
  }
}

/** Decode a grey PNG to its byte values, with no colour management (the bytes are codes, not colours). */
async function decodeGreyPng(blob: Blob): Promise<{ width: number; height: number; codes: Uint8Array } | null> {
  if (typeof createImageBitmap !== 'function' || typeof OffscreenCanvas === 'undefined') return null
  const bmp = await createImageBitmap(blob, { colorSpaceConversion: 'none', premultiplyAlpha: 'none' })
  const canvas = new OffscreenCanvas(bmp.width, bmp.height)
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as OffscreenCanvasRenderingContext2D | null
  if (!ctx) return null
  ctx.drawImage(bmp, 0, 0)
  const rgba = ctx.getImageData(0, 0, bmp.width, bmp.height).data
  const codes = new Uint8Array(bmp.width * bmp.height)
  for (let i = 0; i < codes.length; i++) codes[i] = rgba[i * 4]!
  bmp.close()
  return { width: canvas.width, height: canvas.height, codes }
}

/**
 * The exact pre-codec code at a texel of `dataRegion` (column, row from the
 * top-left), for one frame, or null when the release publishes no exact
 * frames or the file cannot be read. Keeps the last few frames.
 */
export async function exactCodeAt(
  enc: ReleaseEncoding,
  frameIndex: number,
  col: number,
  row: number,
  fetchImpl: typeof fetch = fetch,
): Promise<number | null> {
  const url = exactFrameUrl(enc, frameIndex)
  if (!url) return null
  let p = exactFrameCache.get(url)
  if (!p) {
    p = fetchImpl(url)
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => (blob ? decodeGreyPng(blob) : null))
      .catch((err) => {
        logger.debug('[valueFidelity] exact frame not read', url, err)
        return null
      })
    exactFrameCache.set(url, p)
    if (exactFrameCache.size > EXACT_CACHE_MAX) exactFrameCache.delete(exactFrameCache.keys().next().value!)
  }
  const frame = await p
  const region = sourceEncoding(enc).dataRegion
  if (!frame || frame.width !== region.width || frame.height !== region.height) return null
  if (!(col >= 0 && col < frame.width && row >= 0 && row < frame.height)) return null
  return frame.codes[Math.floor(row) * frame.width + Math.floor(col)]!
}

// ---------------------------------------------------------------- exports

/** One row of an export's header block: a key, then its values. */
export type CustodyRow = readonly [string, ...(string | number | null)[]]

/**
 * What an export's header says about a release's values: which release,
 * from which source, the scale and its step, the streaming error, the
 * regridding and clipping, and the ± rule a reader applies to every value
 * (value error audit, 2026-10-19: no export said any of it). Every error
 * is in the data's own terms (units, a share of the value, or classes);
 * the header never names the codec, its setting, the encoding or "codes"
 * (owner, 2026-11-03: how values are carried is ours).
 */
export function custodyRows(enc: ReleaseEncoding | null | undefined): CustodyRow[] {
  if (!enc) return []
  const src = sourceEncoding(enc)
  const known = fidelityOf(enc)
  const f = known.release
  const rows: CustodyRow[] = []
  if (f?.releaseId) rows.push(['release_id', f.releaseId])
  if (f?.publishedAt) rows.push(['release_published', f.publishedAt])
  if (f?.releaseUrl) rows.push(['release_url', f.releaseUrl])
  const s = known.source
  if (s) {
    if (s.product || s.productId) rows.push(['source_product', s.product ?? s.productId!, s.version])
    if (s.doi) rows.push(['source_doi', s.doi])
    if (s.variable) rows.push(['source_variable', s.variable])
    if (s.distributedAs) rows.push(['source_distributed_as', s.distributedAs, s.classValue])
  }
  rows.push(['value_scale', src.kind === 'luma8-log' ? 'log' : src.kind === 'luma8-classified' ? 'classes' : 'linear', src.vmin, src.vmax])
  const ratio = src.kind === 'luma8-log' ? logCodeRatio(src) : null
  const step = src.kind === 'luma8-linear' ? Math.abs(sourceCodeStep(src)) : null
  if (src.kind === 'luma8-log') rows.push(['quantisation_step_relative', (ratio ?? 1) - 1])
  else if (step !== null) rows.push(['quantisation_step', step])
  const p99 = codecP99Codes(codecOfRelease(enc))
  const basis = p99 ? (p99.basis === 'lossless' ? 'exact' : p99.basis) : null
  if (src.kind === 'luma8-classified') rows.push(['streaming_error_p99_classes', p99 ? p99.codes : 'not stated', basis])
  else if (src.kind === 'luma8-log') rows.push(['streaming_error_p99_relative', p99 ? Math.pow(ratio ?? 1, p99.codes) - 1 : 'not stated', basis])
  else rows.push(['streaming_error_p99', p99 && step !== null ? p99.codes * step : 'not stated', basis])
  if (src.kind !== 'luma8-classified') {
    const steps = (p99 ? p99.codes : 0) + 0.5
    const covers = p99 ? 'rounding and streaming, 99 % of cells' : 'rounding only; the streaming error is not stated'
    rows.push([
      'uncertainty_rule',
      src.kind === 'luma8-log'
        ? `± = ${(steps * ((ratio ?? 1) - 1) * 100).toPrecision(3)} % of the value (${covers})`
        : `± = ${Number((steps * (step ?? 0)).toPrecision(4))} (${covers})`,
    ])
  }
  const rg = known.regrid
  if (rg?.method || rg?.text) rows.push(['regrid', rg.method ?? rg.text!])
  if (rg?.vsNative?.p99 !== null && rg?.vsNative?.p99 !== undefined) rows.push(['regrid_vs_native_p99', rg.vsNative.p99])
  const cl = known.clipping
  if (cl) rows.push(['clipped_share', cl.share ?? (cl.lowShare ?? 0) + (cl.highShare ?? 0)])
  return rows
}

// ------------------------------------------------------------ frame size

let sizeWarned = false

/**
 * Whether the decoded frame is the size the release declares. A player
 * that switches to a lower rendition would hand a smaller frame, and the
 * texel a place maps to (and the calibration row) would then be another
 * one: readings are refused rather than read off the wrong pixel (value
 * error audit, 2026-10-19). True when the release states no size.
 */
export function deliveredSizeMatches(enc: ReleaseEncoding, width: number, height: number): boolean {
  if (!(enc.frameWidth > 0) || !(enc.frameHeight > 0) || !(width > 0) || !(height > 0)) return true
  const ok = width === enc.frameWidth && height === enc.frameHeight
  if (!ok && !sizeWarned) {
    sizeWarned = true
    logger.warn('[valueFidelity] decoded frame is', width, 'x', height, 'but the release declares', enc.frameWidth, 'x', enc.frameHeight, '- values not read')
  }
  return ok
}
